import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import type { ServiceItem } from '../data/servicesData';

interface Props {
  service: ServiceItem;
}

// 每个服务对应的轮播图数据
const heroCarouselImages: Record<string, { src: string; label: string }[]> = {
  wallpaper: [
    {
      src: 'https://public.readdy.ai/ai/img_res/b6d55ee81004c24625f84b02afe9ea44.jpg',
      label: '住宅・マンション — 壁紙クロス施工',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Chinese%20male%20worker%20wearing%20red%20safety%20helmet%20and%20grey%20work%20clothes%20standing%20smoothing%20freshly%20applied%20wallpaper%20on%20office%20wall%20using%20plastic%20smoother%20tool%20commercial%20building%20interior%20renovation%20site%20China%20professional%20wallpaper%20installer%20realistic%20photo%20natural%20lighting%20bright%20workspace&width=1600&height=700&seq=wp-hero-cn-2025-v2-s02&orientation=landscape',
      label: 'リフォーム現場 — クロス張り替え施工',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Chinese%20craftsman%20Asian%20male%20worker%20with%20black%20hair%20wearing%20orange%20work%20vest%20carefully%20aligning%20and%20pressing%20luxury%20patterned%20wallpaper%20onto%20hotel%20corridor%20wall%20professional%20interior%20finishing%20warm%20elegant%20lighting%20high%20end%20hospitality%20renovation%20China%20worker%20realistic%20photo&width=1600&height=700&seq=wp-hero-cn-2025-v2-s03&orientation=landscape',
      label: 'ホテル客室 — 織物クロス仕上げ',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Chinese%20male%20construction%20worker%20wearing%20yellow%20hard%20hat%20and%20blue%20uniform%20using%20long%20handled%20brush%20to%20apply%20wallpaper%20adhesive%20on%20large%20office%20wall%20interior%20renovation%20site%20China%20professional%20worker%20team%20commercial%20building%20bright%20lighting%20realistic%20photo&width=1600&height=700&seq=wp-hero-cn-2025-v2-s04&orientation=landscape',
      label: 'オフィスビル — 機能性クロス施工',
    },
  ],
  board: [
    {
      src: 'https://readdy.ai/api/search-image?query=East%20Asian%20Chinese%20male%20worker%20short%20black%20hair%20yellow%20hard%20hat%20dark%20navy%20uniform%20using%20electric%20screwdriver%20fixing%20white%20gypsum%20plasterboard%20onto%20metal%20stud%20wall%20frame%20indoor%20building%20site%20Japan%20China%20interior%20construction%20realistic%20photo%20close%20up%20bright%20lighting&width=1600&height=700&seq=board-hero-ea-2025-r1a&orientation=landscape',
      label: '大規模施設 — 石膏ボード壁面施工',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=East%20Asian%20Chinese%20male%20worker%20black%20hair%20orange%20safety%20helmet%20grey%20work%20uniform%20standing%20on%20ladder%20using%20power%20drill%20installing%20gypsum%20board%20onto%20ceiling%20metal%20frame%20commercial%20office%20building%20interior%20renovation%20China%20Japan%20worker%20realistic%20photo%20natural%20lighting&width=1600&height=700&seq=board-hero-ea-2025-r2b&orientation=landscape',
      label: 'オフィスビル — 石膏ボード天井施工',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=two%20East%20Asian%20Chinese%20male%20workers%20black%20hair%20white%20hard%20hats%20blue%20work%20uniforms%20teamwork%20lifting%20and%20installing%20large%20mineral%20fiber%20ceiling%20tile%20into%20suspended%20grid%20system%20commercial%20building%20interior%20China%20Japan%20construction%20site%20realistic%20photo%20bright%20workspace&width=1600&height=700&seq=board-hero-ea-2025-r3c&orientation=landscape',
      label: '商業施設 — 岩綿ジプトーン天井施工',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=East%20Asian%20Chinese%20male%20craftsman%20black%20hair%20red%20hard%20hat%20dark%20work%20clothes%20both%20hands%20carefully%20pressing%20decorative%20wood%20grain%20veneer%20panel%20flush%20onto%20interior%20wall%20surface%20hotel%20lobby%20renovation%20site%20China%20Japan%20worker%20close%20up%20realistic%20photo%20warm%20lighting&width=1600&height=700&seq=board-hero-ea-2025-r4d&orientation=landscape',
      label: 'ホテルロビー — 化粧板壁面仕上げ',
    },
  ],
  lgs: [
    {
      src: 'https://readdy.ai/api/search-image?query=East%20Asian%20Chinese%20male%20worker%20short%20black%20hair%20yellow%20hard%20hat%20dark%20blue%20work%20uniform%20using%20electric%20drill%20fixing%20light%20gauge%20steel%20LGS%20metal%20stud%20track%20onto%20concrete%20floor%20indoor%20large%20commercial%20building%20construction%20site%20China%20professional%20interior%20framing%20worker%20close%20up%20realistic%20photo%20bright%20lighting&width=1600&height=700&seq=lgs-hero-ea-2025-r1a&orientation=landscape',
      label: '大規模施設 — LGS下地組み施工',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=East%20Asian%20Chinese%20male%20worker%20black%20hair%20orange%20safety%20helmet%20grey%20uniform%20standing%20on%20scaffolding%20installing%20light%20gauge%20steel%20LGS%20ceiling%20metal%20frame%20grid%20structure%20large%20commercial%20building%20interior%20China%20Japan%20construction%20worker%20realistic%20photo%20wide%20angle%20bright%20workspace&width=1600&height=700&seq=lgs-hero-ea-2025-r2b&orientation=landscape',
      label: '大規模施設 — LGS天井下地・足場施工',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=two%20East%20Asian%20Chinese%20male%20workers%20black%20hair%20white%20hard%20hats%20blue%20work%20uniforms%20measuring%20and%20marking%20chalk%20line%20on%20floor%20for%20LGS%20light%20gauge%20steel%20partition%20wall%20layout%20commercial%20office%20building%20interior%20renovation%20China%20professional%20framing%20workers%20teamwork%20realistic%20photo%20natural%20lighting&width=1600&height=700&seq=lgs-hero-ea-2025-r3c&orientation=landscape',
      label: '大規模施設 — LGS墨出し・割付施工',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=East%20Asian%20Chinese%20male%20worker%20short%20black%20hair%20red%20hard%20hat%20dark%20work%20uniform%20installing%20seismic%20brace%20diagonal%20steel%20rod%20onto%20LGS%20ceiling%20metal%20frame%20earthquake%20resistant%20ceiling%20construction%20commercial%20building%20interior%20China%20Japan%20worker%20close%20up%20realistic%20photo%20warm%20lighting&width=1600&height=700&seq=lgs-hero-ea-2025-r4d&orientation=landscape',
      label: '商業施設 — 耐震ブレース取り付け施工',
    },
  ],
  floor: [
    {
      src: 'https://readdy.ai/api/search-image?query=East%20Asian%20Chinese%20male%20craftsman%20short%20black%20hair%20wearing%20dark%20navy%20blue%20work%20uniform%20and%20red%20hard%20hat%20kneeling%20on%20floor%20carefully%20tapping%20light%20oak%20hardwood%20flooring%20plank%20with%20rubber%20mallet%20bright%20white%20walls%20newly%20renovated%20residential%20apartment%20room%20China%20professional%20flooring%20worker%20close%20up%20realistic%20photo%20natural%20sunlight&width=1600&height=700&seq=floor-carousel-cn-fresh-2025-f01&orientation=landscape',
      label: '住宅・マンション — フローリング施工',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Chinese%20male%20worker%20wearing%20blue%20work%20cap%20and%20dark%20uniform%20kneeling%20on%20floor%20carefully%20unrolling%20and%20pressing%20grey%20vinyl%20long%20sheet%20flooring%20in%20clean%20white%20hospital%20corridor%20medical%20facility%20interior%20China%20professional%20floor%20installation%20worker%20realistic%20photo%20bright%20lighting&width=1600&height=700&seq=floor-carousel-cn-fresh-2025-f03&orientation=landscape',
      label: '医療施設 — 長尺シート施工',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Chinese%20male%20craftsman%20wearing%20yellow%20safety%20helmet%20and%20dark%20work%20clothes%20kneeling%20on%20floor%20carefully%20placing%20and%20aligning%20square%20carpet%20tile%20in%20elegant%20hotel%20room%20warm%20interior%20lighting%20China%20professional%20carpet%20installation%20worker%20realistic%20photo%20close%20up%20precise%20work&width=1600&height=700&seq=floor-carousel-cn-fresh-2025-f04&orientation=landscape',
      label: 'ホテル客室 — タイルカーペット施工',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=East%20Asian%20Chinese%20male%20worker%20short%20black%20hair%20orange%20hard%20hat%20dark%20work%20uniform%20kneeling%20on%20floor%20using%20notched%20trowel%20spreading%20adhesive%20on%20concrete%20subfloor%20before%20laying%20vinyl%20flooring%20commercial%20office%20building%20interior%20renovation%20China%20professional%20flooring%20worker%20close%20up%20realistic%20photo%20bright%20workspace&width=1600&height=700&seq=floor-carousel-cn-fresh-2025-f05&orientation=landscape',
      label: 'オフィスビル — 接着剤塗布・下地処理施工',
    },
  ],
};

function HeroCarousel({ service }: { service: ServiceItem }) {
  const images = heroCarouselImages[service.slug] ?? [{ src: service.heroImage, label: service.title }];
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const goTo = useCallback(
    (index: number) => {
      if (animating || index === current) return;
      setAnimating(true);
      setTimeout(() => {
        setCurrent(index);
        setAnimating(false);
      }, 400);
    },
    [animating, current]
  );

  const prev = useCallback(() => {
    goTo((current - 1 + images.length) % images.length);
  }, [current, images.length, goTo]);

  const next = useCallback(() => {
    goTo((current + 1) % images.length);
  }, [current, images.length, goTo]);

  useEffect(() => {
    const timer = setInterval(() => {
      next();
    }, 5000);
    return () => clearInterval(timer);
  }, [next]);

  return (
    <>
      {/* 轮播图层 */}
      {images.map((img, i) => (
        <img
          key={i}
          src={img.src}
          alt={img.label}
          className="absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700"
          style={{ opacity: i === current ? 1 : 0, zIndex: 0 }}
        />
      ))}

      {/* 渐变遮罩 */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/65" style={{ zIndex: 1 }}></div>

      {/* 中央文字内容 */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8 pointer-events-none" style={{ zIndex: 2 }}>
        <div className="flex items-center gap-3 mb-5">
          <div className="w-8 h-px bg-[#B8960C]"></div>
          <span className="text-[#B8960C] text-xs tracking-[0.3em] font-medium uppercase">Our Services</span>
          <div className="w-8 h-px bg-[#B8960C]"></div>
        </div>
        <h1
          className="text-4xl lg:text-6xl font-bold text-white mb-4 leading-tight"
          style={{ fontFamily: "'Noto Serif JP', serif" }}
        >
          {service.title}
        </h1>
        <p className="text-white/80 text-base max-w-2xl leading-relaxed">{service.shortDesc}</p>
        <div className="flex items-center gap-4 mt-8">
          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center gap-2 border border-white/50 text-white px-6 py-3 text-sm tracking-wider hover:border-[#B8960C] hover:text-[#B8960C] transition-all cursor-pointer whitespace-nowrap pointer-events-auto"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </div>
            事業内容一覧
          </button>
          <a
            href="/#contact"
            className="inline-flex items-center gap-2 bg-[#B8960C] text-white px-6 py-3 text-sm tracking-wider hover:bg-[#9A7D0A] transition-all cursor-pointer whitespace-nowrap pointer-events-auto"
          >
            無料相談・お見積り
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </div>
          </a>
        </div>
      </div>

      {/* 左右切换箭头 */}
      <button
        onClick={prev}
        className="absolute left-6 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/30 hover:bg-[#B8960C] border border-white/30 hover:border-[#B8960C] text-white transition-all duration-300 cursor-pointer"
        style={{ zIndex: 3 }}
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-6 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/30 hover:bg-[#B8960C] border border-white/30 hover:border-[#B8960C] text-white transition-all duration-300 cursor-pointer"
        style={{ zIndex: 3 }}
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      {/* 底部指示器 + 当前图片标签 */}
      <div
        className="absolute bottom-6 left-0 right-0 flex flex-col items-center gap-3"
        style={{ zIndex: 3 }}
      >
        <span className="text-white/70 text-xs tracking-widest px-3 py-1 bg-black/30 border border-white/20">
          {images[current].label}
        </span>
        <div className="flex items-center gap-2">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`transition-all duration-300 cursor-pointer ${
                i === current
                  ? 'w-8 h-1.5 bg-[#B8960C]'
                  : 'w-3 h-1.5 bg-white/40 hover:bg-white/70'
              }`}
            />
          ))}
        </div>
      </div>
    </>
  );
}

function FeatureCard({ feature: f, index: i }: { feature: ServiceItem['features'][0]; index: number }) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className="bg-white p-6 rounded-sm border border-gray-100 hover:shadow-lg transition-all duration-300"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: `opacity 0.6s ease ${i * 0.08}s, transform 0.6s ease ${i * 0.08}s`,
      }}
    >
      <div className="w-10 h-10 flex items-center justify-center bg-[#B8960C]/10 rounded-md mb-4">
        <i className={`${f.icon} text-xl text-[#B8960C]`}></i>
      </div>
      <h3 className="text-sm font-bold text-[#1A1A1A] mb-2" style={{ fontFamily: "'Noto Serif JP', serif" }}>
        {f.title}
      </h3>
      <p className="text-xs text-gray-500 leading-relaxed">{f.desc}</p>
    </div>
  );
}

function FaqItem({ item }: { item: { q: string; a: string } }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-sm overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left bg-white hover:bg-[#F8F6F1] transition-colors cursor-pointer"
      >
        <div className="flex items-start gap-3">
          <span className="text-[#B8960C] font-bold text-sm mt-0.5 flex-shrink-0">Q.</span>
          <span className="text-sm font-medium text-[#1A1A1A] leading-relaxed">{item.q}</span>
        </div>
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 ml-4">
          <i className={`text-[#B8960C] text-base transition-transform duration-300 ${open ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
        </div>
      </button>
      {open && (
        <div className="px-6 py-4 bg-[#F8F6F1] border-t border-gray-100">
          <div className="flex items-start gap-3">
            <span className="text-[#B8960C] font-bold text-sm mt-0.5 flex-shrink-0">A.</span>
            <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function WorkPointCard({ point, index }: { point: ServiceItem['workPoints'][0]; index: number }) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className="bg-white rounded-sm border border-gray-100 p-6 hover:shadow-lg transition-all duration-300 group"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: `opacity 0.6s ease ${index * 0.1}s, transform 0.6s ease ${index * 0.1}s`,
      }}
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 flex items-center justify-center bg-[#B8960C]/10 rounded-md flex-shrink-0 group-hover:bg-[#B8960C]/20 transition-colors">
          <i className={`${point.icon} text-xl text-[#B8960C]`}></i>
        </div>
        <div>
          <h4
            className="text-sm font-bold text-[#1A1A1A] mb-2 leading-tight"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            {point.title}
          </h4>
          <p className="text-xs text-gray-500 leading-relaxed">{point.detail}</p>
        </div>
      </div>
    </div>
  );
}

export default function ServiceDetail({ service }: Props) {
  const navigate = useNavigate();
  const { ref: overviewRef, isVisible: overviewVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: featuresRef, isVisible: featuresVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: worksRef, isVisible: worksVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: processRef, isVisible: processVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: workPointsRef, isVisible: workPointsVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: faqRef, isVisible: faqVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <main>
      {/* Hero 轮播 */}
      <section className="relative w-full h-[520px] overflow-hidden">
        <HeroCarousel service={service} />
      </section>

      {/* Breadcrumb */}
      <div className="bg-[#F8F6F1] border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-8 py-3 flex items-center gap-2 text-xs text-gray-400">
          <a href="/" className="hover:text-[#B8960C] transition-colors cursor-pointer">ホーム</a>
          <i className="ri-arrow-right-s-line"></i>
          <a href="/#services" className="hover:text-[#B8960C] transition-colors cursor-pointer">事業内容</a>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-[#B8960C]">{service.title}</span>
        </div>
      </div>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div
          ref={overviewRef as React.RefObject<HTMLDivElement>}
          className="max-w-7xl mx-auto px-8"
          style={{
            opacity: overviewVisible ? 1 : 0,
            transform: overviewVisible ? 'translateY(0)' : 'translateY(32px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-px bg-[#B8960C]"></div>
                <span className="text-[#B8960C] text-xs tracking-[0.3em] font-medium uppercase">Overview</span>
              </div>
              <h2
                className="text-3xl lg:text-4xl font-bold text-[#1A1A1A] mb-6 leading-tight"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                サービス概要
              </h2>
              <p className="text-gray-600 text-base leading-relaxed">{service.overview}</p>
              <div className="mt-8 flex items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center bg-[#B8960C]/10 rounded-md">
                  <i className={`${service.icon} text-2xl text-[#B8960C]`}></i>
                </div>
                <div>
                  <div className="text-xs text-gray-400 tracking-wider">専門工事</div>
                  <div className="font-bold text-[#1A1A1A] text-sm" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                    {service.title}
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {service.workImages.slice(0, 1).map((img, i) => (
                <div key={i} className="col-span-2 h-56 overflow-hidden rounded-sm">
                  <img
                    src={img.src}
                    alt={img.caption}
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
              ))}
              {service.workImages.slice(1, 3).map((img, i) => (
                <div key={i} className="h-40 overflow-hidden rounded-sm">
                  <img
                    src={img.src}
                    alt={img.caption}
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-[#F8F6F1]">
        <div className="max-w-7xl mx-auto px-8">
          <div
            ref={featuresRef as React.RefObject<HTMLDivElement>}
            className="text-center mb-14"
            style={{
              opacity: featuresVisible ? 1 : 0,
              transform: featuresVisible ? 'translateY(0)' : 'translateY(32px)',
              transition: 'opacity 0.7s ease, transform 0.7s ease',
            }}
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-px bg-[#B8960C]"></div>
              <span className="text-[#B8960C] text-xs tracking-[0.3em] font-medium uppercase">Features</span>
              <div className="w-8 h-px bg-[#B8960C]"></div>
            </div>
            <h2
              className="text-3xl lg:text-4xl font-bold text-[#1A1A1A]"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              サービスの特長
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.features.map((f, i) => (
              <FeatureCard key={i} feature={f} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Work Gallery */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div
            ref={worksRef as React.RefObject<HTMLDivElement>}
            className="text-center mb-14"
            style={{
              opacity: worksVisible ? 1 : 0,
              transform: worksVisible ? 'translateY(0)' : 'translateY(32px)',
              transition: 'opacity 0.7s ease, transform 0.7s ease',
            }}
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-px bg-[#B8960C]"></div>
              <span className="text-[#B8960C] text-xs tracking-[0.3em] font-medium uppercase">Gallery</span>
              <div className="w-8 h-px bg-[#B8960C]"></div>
            </div>
            <h2
              className="text-3xl lg:text-4xl font-bold text-[#1A1A1A]"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              施工実績ギャラリー
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {service.workImages.map((img, i) => (
              <div key={i} className="group overflow-hidden rounded-sm shadow-md">
                <div className="w-full h-52 overflow-hidden">
                  <img
                    src={img.src}
                    alt={img.caption}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="bg-white px-4 py-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500 leading-relaxed">{img.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-8">
          <div
            ref={processRef as React.RefObject<HTMLDivElement>}
            style={{
              opacity: processVisible ? 1 : 0,
              transform: processVisible ? 'translateY(0)' : 'translateY(32px)',
              transition: 'opacity 0.7s ease, transform 0.7s ease',
            }}
          >
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-8 h-px bg-[#B8960C]"></div>
                <span className="text-[#B8960C] text-xs tracking-[0.3em] font-medium uppercase">Process</span>
                <div className="w-8 h-px bg-[#B8960C]"></div>
              </div>
              <h2
                className="text-3xl lg:text-4xl font-bold text-white mb-4"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                施工の流れ
              </h2>
              <p className="text-white/50 text-sm mt-3">各工程の詳細と品質管理ポイントをご説明します</p>
            </div>

            {/* 竖向时间轴布局 */}
            <div className="max-w-4xl mx-auto">
              {service.process.map((p, i) => (
                <div key={i} className="flex gap-6 mb-0 group">
                  {/* 左侧步骤轴 */}
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="w-14 h-14 flex items-center justify-center bg-[#B8960C] rounded-full shadow-lg z-10">
                      <span
                        className="text-white font-bold text-sm"
                        style={{ fontFamily: "'Noto Serif JP', serif" }}
                      >
                        {p.step}
                      </span>
                    </div>
                    {i < service.process.length - 1 && (
                      <div className="w-px flex-1 bg-[#B8960C]/30 my-1 min-h-[2rem]"></div>
                    )}
                  </div>

                  {/* 右侧内容卡片 */}
                  <div className={`flex-1 pb-8 ${i === service.process.length - 1 ? 'pb-0' : ''}`}>
                    <div className="bg-white/5 border border-white/10 rounded-sm p-5 hover:bg-white/10 hover:border-[#B8960C]/40 transition-all duration-300">
                      <h4
                        className="text-base font-bold text-white mb-2 leading-tight"
                        style={{ fontFamily: "'Noto Serif JP', serif" }}
                      >
                        {p.title}
                      </h4>
                      <p className="text-white/60 text-sm leading-relaxed mb-3">{p.desc}</p>
                      {p.note && (
                        <div className="flex items-start gap-2 bg-[#B8960C]/10 border-l-2 border-[#B8960C] px-3 py-2 rounded-r-sm">
                          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-lightbulb-line text-[#B8960C] text-xs"></i>
                          </div>
                          <p className="text-[#B8960C]/90 text-xs leading-relaxed">{p.note}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Work Points — 施工ポイント */}
      <section className="py-20 bg-[#F8F6F1]">
        <div className="max-w-7xl mx-auto px-8">
          <div
            ref={workPointsRef as React.RefObject<HTMLDivElement>}
            className="text-center mb-14"
            style={{
              opacity: workPointsVisible ? 1 : 0,
              transform: workPointsVisible ? 'translateY(0)' : 'translateY(32px)',
              transition: 'opacity 0.7s ease, transform 0.7s ease',
            }}
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-px bg-[#B8960C]"></div>
              <span className="text-[#B8960C] text-xs tracking-[0.3em] font-medium uppercase">Key Points</span>
              <div className="w-8 h-px bg-[#B8960C]"></div>
            </div>
            <h2
              className="text-3xl lg:text-4xl font-bold text-[#1A1A1A]"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              施工の重要ポイント
            </h2>
            <p className="text-gray-400 text-sm mt-3">品質を左右する技術的なこだわりをご紹介します</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {service.workPoints.map((point, i) => (
              <WorkPointCard key={i} point={point} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-8">
          <div
            ref={faqRef as React.RefObject<HTMLDivElement>}
            style={{
              opacity: faqVisible ? 1 : 0,
              transform: faqVisible ? 'translateY(0)' : 'translateY(32px)',
              transition: 'opacity 0.7s ease, transform 0.7s ease',
            }}
          >
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-8 h-px bg-[#B8960C]"></div>
                <span className="text-[#B8960C] text-xs tracking-[0.3em] font-medium uppercase">FAQ</span>
                <div className="w-8 h-px bg-[#B8960C]"></div>
              </div>
              <h2
                className="text-3xl lg:text-4xl font-bold text-[#1A1A1A]"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                よくあるご質問
              </h2>
            </div>
            <div className="space-y-4">
              {service.faq.map((item, i) => (
                <FaqItem key={i} item={item} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="max-w-3xl mx-auto px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-8 h-px bg-[#B8960C]"></div>
            <span className="text-[#B8960C] text-xs tracking-[0.3em] font-medium uppercase">Contact Us</span>
            <div className="w-8 h-px bg-[#B8960C]"></div>
          </div>
          <h2
            className="text-3xl lg:text-4xl font-bold text-white mb-4"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            {service.title}のご相談はこちら
          </h2>
          <p className="text-white/60 text-sm leading-relaxed mb-8">
            お見積り・ご相談は無料です。お気軽にお問い合わせください。
          </p>
          <a
            href="/#contact"
            className="inline-flex items-center gap-3 bg-[#B8960C] text-white px-10 py-4 text-sm font-medium tracking-widest hover:bg-[#9A7D0A] transition-colors cursor-pointer whitespace-nowrap"
          >
            無料相談・お見積りはこちら
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </div>
          </a>
        </div>
      </section>
    </main>
  );
}
